import { animate, animateChild, query, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Network } from '@capacitor/network';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonSpinner,
  IonTitle,
  IonToolbar,
  ModalController,
  Platform,
  ToastController
} from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import moment from 'moment';
import { MomentModule } from 'ngx-moment';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, from, of, timer } from 'rxjs';
import { catchError, filter, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  CreateIntentResponse,
  Payment,
  PaymentService,
  PaymentStatus,
  PaymentViewModel
} from 'src/app/services/payment.service';
import { ParentHubStripe } from 'src/plugins/parent-hub-stripe';
import * as fromStore from '../../store';
import { visitAppStore } from '../../store';
import { EditPaymentAmountModalComponent } from '../edit-payment-amount-modal/edit-payment-amount-modal.component';
import { PublisherAvatarComponent } from '../publisher-avatar/publisher-avatar.component';
import {
  PaymentFormViewModel,
  PaymentState,
  StudentPaymentFormNativeComponent
} from '../student-payment-form-native/student-payment-form-native.component';
import { StudentPaymentFormComponent } from '../student-payment-form/student-payment-form.component';
import { StudentPaymentState, StudentPaymentStore } from './student-payment.store';

export enum Outcome {
  InitialState,
  Succeeded,
  Failed,
  Canceled
}

@Component({
  selector: 'app-student-payment-modal',
  templateUrl: './student-payment-modal.component.html',
  styleUrls: ['./student-payment-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StudentPaymentStore],
  animations: [
    trigger('slideUpFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(15%)' }),
        animate('200ms ease-in', style({ opacity: 1, transform: 'translateY(-3%)' })),
        animate('150ms ease-in', style({ opacity: 1, transform: 'translateY(0%)' }))
      ])
    ]),
    trigger('slideUpFadeInWithChild', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(15%)' }),
        animate('200ms ease-in', style({ opacity: 1, transform: 'translateY(-3%)' })),
        animate('150ms ease-in', style({ opacity: 1, transform: 'translateY(0%)' })),
        query('@delayedSlideUpFadeIn', animateChild())
      ])
    ]),
    trigger('delayedSlideUpFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(15%)' }),
        animate('400ms 100ms ease-in', style({ opacity: 1, transform: 'translateY(0%)' }))
      ])
    ])
  ],
  imports: [
    NgTemplateOutlet,
    StudentPaymentFormNativeComponent,
    StudentPaymentFormComponent,
    PublisherAvatarComponent,
    AsyncPipe,
    MomentModule,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonGrid,
    IonSpinner,
    IonCard
  ]
})
export class StudentPaymentModalComponent implements OnInit, OnDestroy {
  @Input() schoolId: number;
  @Input() studentId: number;
  @Input() paymentId: number;
  @Input() loadingFromList: boolean;
  readonly vm$: Observable<StudentPaymentState> = this.studentPaymentStore.vm$;
  viewModelSubject = new BehaviorSubject<PaymentFormViewModel>({ state: PaymentState.Initialising });
  viewModel$ = this.viewModelSubject.asObservable();
  noIntentMessageCode: string = null;
  noIntentMessage: string = null;
  wentThroughConfirmation = false;
  paymentStatus = PaymentStatus;
  PaymentState = PaymentState;
  native = false;
  intentResponse$: Observable<any>;
  destroyed$: Subject<boolean>;
  paymentIntentLoading: boolean;
  paymentComplete: boolean;
  paymentMethodId: string;
  studentForename: string;
  sub: Subscription;
  schoolName: string;
  paymentKey: string;
  listener: any;
  canRetryIntent = false;
  paidWithGooglePay = false;
  updateRequired = false;

  constructor(
    private studentPaymentStore: StudentPaymentStore,
    private toastController: ToastController,
    private paymentService: PaymentService,
    private modalCtrl: ModalController,
    private store: Store<fromStore.AppState>,
    private cd: ChangeDetectorRef,
    public platform: Platform
  ) {
    this.sub = this.platform.backButton.subscribeWithPriority(9999, () => {
      document.addEventListener('backbutton', this.backButtonHandler, false);
      this.onDismiss();
    });

    this.viewModel$.pipe(tap(console.log)).subscribe();
  }

  private backButtonHandler(event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  ngOnInit(): void {
    this.destroyed$ = new Subject<boolean>();

    this.native = this.platform.is('hybrid');

    this.store
      .select(fromStore.getSchoolAttendanceViewModel(this.schoolId))
      .pipe(take(1))
      .subscribe((school) => {
        if (school.hasResponseSlips) {
          this.updateRequired = true;
          this.cd.detectChanges();
        } else {
          this.studentPaymentStore.loadPayment({
            schoolId: this.schoolId,
            studentId: this.studentId,
            paymentId: this.paymentId,
            loadingFromList: this.loadingFromList
          });
        }
      });

    this.store
      .select(fromStore.getPaymentMethodId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((paymentMethodId) => {
        this.paymentMethodId = paymentMethodId;
      });

    this.getPaymentSub();

    this.studentPaymentStore.getPayment$
      .pipe(
        filter(payment => !!payment),
        takeUntil(this.destroyed$)
      )
      .subscribe((payment) => {
        if (payment.paymentBeingConfirmed) {
          this.paymentIntentLoading = false;

          const vm = this.viewModelSubject.getValue();
          this.viewModelSubject.next({
            ...vm,
            state: PaymentState.ConfirmingPayment
          });

          this.wentThroughConfirmation = true;

          this.cd.detectChanges();
        } else if (this.wentThroughConfirmation && !payment.paymentBeingConfirmed) {
          this.paymentIntentLoading = false;
          const vm = this.viewModelSubject.getValue();
          this.viewModelSubject.next({
            ...vm,
            state: this.PaymentState.PaymentConfirmed
          });
          this.cd.detectChanges();
        } else if (payment.balance <= 0) {
          this.paymentIntentLoading = false;

          this.noIntentMessage = 'REQUEST IS FULLY PAID';
          this.noIntentMessageCode = 'FullyPaid';

          this.cd.detectChanges();
        } else if (payment.status === PaymentStatus.Finished) {
          this.paymentIntentLoading = false;
          this.noIntentMessage = 'REQUEST IS NOW CLOSED';
          this.noIntentMessageCode = 'PaymentNotActive';
          this.cd.detectChanges();
        } else if (payment.removedOn) {
          this.paymentIntentLoading = false;
          this.noIntentMessage =
            'This request is no longer active for your child. Please contact the school for more information.';
          this.noIntentMessageCode = 'StudentNotValid';
          this.cd.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.destroyed$) {
      this.destroyed$.next(true);
      this.destroyed$.complete();
    }

    if (this.listener) {
      this.listener.remove();
    }

    this.sub.unsubscribe();
    document.removeEventListener('backbutton', this.backButtonHandler, false);
  }

  onChooseMethod(viewModel: PaymentFormViewModel): void {
    if (this.platform.is('android')) {
      Network.getStatus().then((status) => {
        if (status.connected === false) {
          this.showToast('Please check your internet connection and try again later', 'noInternetToast');
          return;
        } else {
          ParentHubStripe.choosePaymentMethod({
            paymentMethodId: viewModel.method?.paymentMethodId
          }).catch((error) => {
            console.log(error);
          });
        }
      });
    } else {
      ParentHubStripe.choosePaymentMethod({
        paymentMethodId: viewModel.method?.paymentMethodId
      }).catch((error) => {
        console.log(error);
      });
    }
  }

  onConfirm(viewModel: PaymentFormViewModel, payment: Payment): void {
    this.viewModelSubject.next({
      ...viewModel,
      error: null,
      state: PaymentState.ProcessingPayment
    });

    combineLatest([
      timer(1000),
      from(
        ParentHubStripe.confirmPayment({
          publishableKey: this.paymentKey,
          intentSecretKey: viewModel.intent.intentSecretKey,
          paymentMethodId: viewModel.method.paymentMethodId
        })
      ).pipe(catchError(error => of({ code: error.message, status: 'failure', outcome: 2 })))
    ])
      .pipe(take(1))
      .subscribe(
        ([, result]: [number, any]) => {
          // console.log('confirmPayment result', result);
          let state: PaymentState;
          let error;

          if (this.paymentCompleted(result)) {
            state = PaymentState.ConfirmingPayment;
            error = null;

            this.store.dispatch(
              fromStore.addIntent({
                intent: {
                  id: viewModel.intent.intentId,
                  tenant: this.schoolId,
                  studentId: this.studentId,
                  paymentId: this.paymentId,
                  nextCheckTimestamp: 0,
                  attempt: 0
                }
              })
            );
          } else if (result.outcome === Outcome.Failed || (result.outcome === Outcome.Canceled && result.code)) {
            state = PaymentState.Initialised;
            error = result.code;
          } else {
            state = PaymentState.Initialised;
            error = null;
          }

          this.viewModelSubject.next({
            ...viewModel,
            state,
            error
          });

          if (error?.includes('offline')) {
            this.showToast('Please check your internet connection and try again later', 'noInternetToast');
          }

          this.cd.detectChanges();
        },
        (error) => {
          this.viewModelSubject.next({
            ...viewModel,
            state: PaymentState.Initialised,
            error: error ?? 'An unknown error occurred'
          });

          this.cd.detectChanges();
        }
      );
  }

  onPayWithApplePay(viewModel: PaymentFormViewModel, payment: Payment): void {
    this.viewModelSubject.next({
      ...viewModel,
      error: null
    });

    ParentHubStripe.payWithApplePay({
      publishableKey: this.paymentKey,
      intentSecretKey: viewModel.intent.intentSecretKey,
      totalPrice: viewModel.intent.amount,
      currencyCode: 'GBP',
      schoolName: this.schoolName
    }).then(
      (result) => {
        // console.log('confirmPayment result', result);
        let state: PaymentState;
        let error;

        if (this.paymentCompleted(result)) {
          state = PaymentState.ConfirmingPayment;
          error = null;

          this.store.dispatch(
            fromStore.addIntent({
              intent: {
                id: viewModel.intent.intentId,
                tenant: this.schoolId,
                studentId: this.studentId,
                paymentId: this.paymentId,
                nextCheckTimestamp: 0,
                attempt: 0
              }
            })
          );
        } else if (result.outcome === Outcome.Failed) {
          state = PaymentState.Initialised;
          error = result.code;
        } else {
          state = PaymentState.Initialised;
          error = null;
        }

        this.viewModelSubject.next({
          ...viewModel,
          state,
          error
        });

        this.cd.detectChanges();
      },
      (error) => {
        // console.log(error);

        this.viewModelSubject.next({
          ...viewModel,
          error: error.message
        });

        this.cd.detectChanges();
      }
    );
  }

  onPayWithGooglePay(viewModel: PaymentFormViewModel, payment: Payment): void {
    this.paidWithGooglePay = false;

    const previousState = viewModel.state;

    this.viewModelSubject.next({
      ...viewModel,
      error: null
    });

    ParentHubStripe.payWithGooglePay({
      publishableKey: this.paymentKey,
      intentSecretKey: viewModel.intent.intentSecretKey,
      totalPrice: viewModel.intent.amount,
      currencyCode: 'GBP'
    }).then(
      (result) => {
        // console.log(result);
        if (!result.paymentMethodId) {
          this.paidWithGooglePay = false;

          // We probably got cancelled
          this.viewModelSubject.next({
            ...viewModel,
            state: previousState
          });
        } else {
          this.paidWithGooglePay = true;

          this.viewModelSubject.next({
            ...viewModel,
            error: null,
            state: PaymentState.ProcessingPayment
          });

          ParentHubStripe.confirmPayment({
            publishableKey: this.paymentKey,
            intentSecretKey: viewModel.intent.intentSecretKey,
            paymentMethodId: result.paymentMethodId
          }).then(
            (resultIn) => {
              let state: PaymentState;
              let error;

              if (this.paymentCompleted(result)) {
                state = PaymentState.ConfirmingPayment;
                error = null;

                this.store.dispatch(
                  fromStore.addIntent({
                    intent: {
                      id: viewModel.intent.intentId,
                      tenant: this.schoolId,
                      studentId: this.studentId,
                      paymentId: this.paymentId,
                      nextCheckTimestamp: 0,
                      attempt: 0
                    }
                  })
                );
              } else if (result.outcome === Outcome.Failed) {
                this.paidWithGooglePay = false;
                state = PaymentState.Initialised;
                error = result.code;
              } else {
                this.paidWithGooglePay = false;
                state = PaymentState.Initialised;
                error = null;
              }

              this.viewModelSubject.next({
                ...viewModel,
                state,
                error
              });

              this.cd.detectChanges();
            },
            (error) => {
              this.paidWithGooglePay = false;

              // console.log(error);

              this.viewModelSubject.next({
                ...viewModel,
                error: error.message
              });

              this.cd.detectChanges();
            }
          );
        }
      },
      (error) => {
        this.paidWithGooglePay = false;

        // console.log(error);

        this.viewModelSubject.next({
          ...viewModel,
          error: error.message
        });

        this.cd.detectChanges();
      }
    );
  }

  onPaymentComplete(vm: PaymentFormViewModel): void {
    this.paymentComplete = true;
    this.store.dispatch(
      fromStore.addIntent({
        intent: {
          id: vm.intent.intentId,
          tenant: this.schoolId,
          studentId: this.studentId,
          paymentId: this.paymentId,
          nextCheckTimestamp: 0,
          attempt: 0
        }
      })
    );
  }

  async onEditAmount(vm: PaymentFormViewModel, payment: Payment): Promise<void> {
    const operation = (amount: number): Observable<CreateIntentResponse> => {
      return this.paymentService.createIntent(this.schoolId, this.studentId, this.paymentId, amount);
    };
    const modal = await this.modalCtrl.create({
      component: EditPaymentAmountModalComponent,
      componentProps: {
        paymentTitle: payment.title,
        balance: payment.balance,
        minimumAmount: payment.minimumAmount,
        intentRequest: operation
      },
      backdropDismiss: false
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    const intentResponse = data.intentResponse as CreateIntentResponse;
    this.viewModelSubject.next({
      ...this.viewModelSubject.getValue(),
      intent: intentResponse
    });
  }

  isNegative(amount: number): boolean {
    return amount < 0;
  }

  getPounds(amount: number): string {
    return (
      Math.abs(amount / 100)
        .toString()
        .split('.')[0] || '00'
    );
  }

  getPences(amount: number): string {
    return (amount / 100).toFixed(2).toString().split('.')[1] || '00';
  }

  isOverdue(dueOn: Date, payment: PaymentViewModel): boolean {
    return (
      moment.utc().isAfter(moment.utc(dueOn).add(1, 'days')) &&
      payment.status === PaymentStatus.Active &&
      payment.balance > 0
    );
  }

  retryLoad(): void {
    this.noIntentMessage = null;
    this.paymentIntentLoading = true;
    this.cd.detectChanges();

    this.getPaymentSub();

    this.studentPaymentStore.loadPayment({
      schoolId: this.schoolId,
      studentId: this.studentId,
      paymentId: this.paymentId,
      loadingFromList: this.loadingFromList
    });
  }

  onVisitAppStore(): void {
    this.store.dispatch(visitAppStore());
  }

  onDismiss(): void {
    this.modalCtrl.dismiss();
  }

  private getPaymentSub(): void {
    this.studentPaymentStore.getPayment$
      .pipe(
        filter(payment => !!payment),
        withLatestFrom(this.store.select(fromStore.getSchoolAttendanceViewModel(this.schoolId))),
        withLatestFrom(this.store.select(fromStore.getStudent(this.studentId))),
        take(1)
      )
      .subscribe(([[payment, school], student]) => {
        this.schoolName = school.name;
        this.studentForename = student.forename;
        this.paymentKey = school.paymentKey;
        this.noIntentMessage = null;

        if (payment.paymentBeingConfirmed) {
          this.paymentIntentLoading = false;

          const vm = this.viewModelSubject.getValue();
          this.viewModelSubject.next({
            ...vm,
            state: PaymentState.ConfirmingPayment
          });

          this.wentThroughConfirmation = true;

          this.cd.detectChanges();
        } else if (payment.balance <= 0) {
          this.paymentIntentLoading = false;

          if (this.wentThroughConfirmation) {
            const vm = this.viewModelSubject.getValue();
            this.viewModelSubject.next({
              ...vm,
              state: this.PaymentState.PaymentConfirmed
            });
          } else {
            this.noIntentMessage = 'REQUEST FULLY PAID';
            this.noIntentMessageCode = 'FullyPaid';
          }

          this.cd.detectChanges();
        } else if (payment.status === PaymentStatus.Finished) {
          this.paymentIntentLoading = false;
          this.noIntentMessage = 'REQUEST NOW CLOSED';
          this.noIntentMessageCode = 'PaymentNotActive';
          this.cd.detectChanges();
        } else if (payment.removedOn) {
          this.paymentIntentLoading = false;
          this.noIntentMessage =
            'This request is no longer active for your child. Please contact the school for more information.';
          this.noIntentMessageCode = 'StudentNotValid';
          this.cd.detectChanges();
        } else {
          const vm = this.viewModelSubject.getValue();
          this.viewModelSubject.next({
            ...vm,
            canEditAmount: !!payment.minimumAmount
          });
          this.loadIntent(payment.balance);
        }
      });
  }

  loadIntent(amount: number): void {
    this.noIntentMessage = null;
    this.paymentIntentLoading = true;
    this.canRetryIntent = false;
    this.cd.detectChanges();

    this.intentResponse$ = this.paymentService.createIntent(this.schoolId, this.studentId, this.paymentId, amount);

    combineLatest([
      timer(1000),
      this.intentResponse$.pipe(
        catchError((error) => {
          let intentError: string;

          if (error?.status === 0) {
            intentError = `It looks like you're offline. Please check your connection and try again.`;
            this.noIntentMessageCode = 'Offline';
          } else if (error?.error?.ErrorCode) {
            switch (error.error.ErrorCode) {
              // PaymentNotActive
              case 2000:
                intentError = 'REQUEST NOW CLOSED';
                this.noIntentMessageCode = 'PaymentNotActive';
                break;

              // StudentNotValid
              case 2001:
                intentError =
                  'This request is no longer active for your child. Please contact the school for more information.';
                this.noIntentMessageCode = 'StudentNotValid';
                break;

              // PaymentInProgress
              case 2002:
                intentError = `You're unable to pay right now as another contact is viewing this request.

Please try again in a couple of minutes.`;
                this.noIntentMessageCode = 'PaymentInProgress';
                break;

              // SchoolSubscriberError
              case 2003:
                intentError = 'Error: SchoolSubscriberError';
                this.noIntentMessageCode = 'SchoolSubscriberError';
                break;

              // FullyPaid
              case 2004:
                intentError = 'REQUEST IS FULLY PAID';
                this.noIntentMessageCode = 'FullyPaid';
                break;

              // Something went wrong
              default:
                intentError =
                  'Unfortunately, something went wrong during loading. Please leave the request and try again. ErrorCode: ' +
                  error.error.ErrorCode;
                this.noIntentMessageCode = 'SomethingWentWrong';
                break;
            }
          } else {
            intentError = 'Unfortunately, something went wrong during loading. Please leave the request and try again.';
            this.noIntentMessageCode = 'SomethingWentWrong';
          }

          return of(intentError);
        })
      )
    ]).subscribe(async ([, requestResult]: [number, any]) => {
      if (requestResult.ephemeralKey && requestResult.intentSecretKey) {
        // When its an intent and the information we need is there

        this.viewModelSubject.next({
          ...this.viewModelSubject.getValue(),
          state: PaymentState.Initialising,
          intent: requestResult
        });

        if (this.native) {
          await ParentHubStripe.stripeInit({
            publishableKey: this.paymentKey
          });

          if (this.platform.is('android')) {
            await ParentHubStripe.initialiseGooglePay().then(initGooglePayResult =>
              this.viewModelSubject.next({
                ...this.viewModelSubject.getValue(),
                state: PaymentState.Initialised,
                canPayWithGooglePay: initGooglePayResult.canPayByGooglePay
              })
            );
          }

          if (this.platform.is('ios')) {
            ParentHubStripe.initialiseApplePay().then(initApplePayResult =>
              this.viewModelSubject.next({
                ...this.viewModelSubject.getValue(),
                state: PaymentState.Initialised,
                canPayWithApplePay: initApplePayResult.canPayByApplePay
              })
            );
          }

          await ParentHubStripe.initialiseCustomerSession({
            ephemeralKey: requestResult.ephemeralKey
          });

          const result = await ParentHubStripe.findPaymentMethod({
            paymentMethodId: this.paymentMethodId
          });

          if (result && !_.isEmpty(result)) {
            const vm = this.viewModelSubject.getValue();
            this.viewModelSubject.next({
              ...vm,
              method: {
                paymentMethodId: result.paymentMethodId,
                cardBrand: result.cardBrand,
                cardLast4: result.cardLast4
              }
            });
          }

          this.listener = ParentHubStripe.addListener('paymentMethodChanged', (innerResult: any): void => {
            // console.log('paymentMethodChanged was fired', result);
            const vm = this.viewModelSubject.getValue();
            // console.log('viewmodel console log', vm);
            this.viewModelSubject.next({
              ...vm,
              error: null,
              method: {
                paymentMethodId: innerResult.paymentMethodId,
                cardBrand: innerResult.cardBrand,
                cardLast4: innerResult.cardLast4
              }
            });

            this.store.dispatch(new fromStore.UpdatePaymentMethodId({ paymentMethodId: innerResult.paymentMethodId }));
          });
        } else {
          this.viewModelSubject.next({
            ...this.viewModelSubject.getValue(),
            state: PaymentState.Initialised,
            intent: requestResult
          });
        }

        this.paymentIntentLoading = false;
        this.cd.detectChanges();
      } else if (typeof requestResult === 'string') {
        // When its an error

        if (requestResult.includes('offline')) {
          this.canRetryIntent = true;
          this.showToast('Please check your internet connection and try again later', 'noInternetToast');
        }

        this.noIntentMessage = requestResult;
        this.paymentIntentLoading = false;
        this.cd.detectChanges();
      }
    });
  }

  getNoIntentMessageClass(): string {
    switch (this.noIntentMessageCode) {
      case 'PaymentNotActive':
        return 'grey';
      case 'StudentNotValid':
        return 'orange';
      case 'PaymentInProgress':
        return 'orange textLeft';
      case 'SchoolSubscriberError':
        return 'red';
      case 'FullyPaid':
        return 'green';
      case 'SomethingWentWrong':
        return 'red';
      case 'Offline':
        return 'red tryAgain';
      default:
        return '';
    }
  }

  getNoIntentMessageIcon(): string {
    switch (this.noIntentMessageCode) {
      case 'PaymentNotActive':
        return '/assets/icon/white-no-entry-outline.svg';
      case 'StudentNotValid':
        return '';
      case 'PaymentInProgress':
        return '/assets/icon/orange-eye-outline.svg';
      case 'SchoolSubscriberError':
        return '';
      case 'FullyPaid':
        return '/assets/icon/white-checkmark-circle-outline.svg';
      case 'SomethingWentWrong':
        return '';
      default:
        return '';
    }
  }

  paymentCompleted(object: any): boolean {
    return (
      (Object.keys(object).length !== 0 &&
        object.outcome !== Outcome.InitialState &&
        object.outcome !== Outcome.Failed &&
        object.outcome !== Outcome.Canceled) ||
        object.result === 'succeeded' ||
        object.status === 'succeeded'
    );
  }

  showToast(toastMessage: string, toastCssClass: string): void {
    const toast = this.toastController.create({
      message: toastMessage,
      duration: 2000,
      position: 'top',
      cssClass: toastCssClass
    });

    toast.then(t => t.present());
  }
}
